/**
 * The look the app is drawn in: which colour profile's colours the theme is
 * built from right now.
 *
 * Split from LookContext.tsx for the same reason selectedTenant.ts is: a file
 * exporting both a component and a non-component loses fast refresh.
 */

import { createContext } from 'react';
import { buildTheme, paletteFor, readColours, theme as meridian, type ProfileColours, type Theme } from '../ui';

export interface LookValue {
  /** The colours the theme is built from; empty is Meridian. */
  colours: ProfileColours;
  /** Rebuild the theme from these colours and remember them for the next start, signed in or not. */
  applyColours: (colours: ProfileColours) => void;
}

export const LookContext = createContext<LookValue | null>(null);

const STORAGE_KEY = 'mlp.look';

export interface LookStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

/** This browser's storage, or none where even reaching it throws. */
export const browserStorage = (): LookStorage | undefined => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

/**
 * The look last applied in this browser, so the loading and sign-in screens
 * are already in it, also after a sign-out. Colours only, never who they
 * belonged to. Storage can be missing or throw (a private window, blocked site
 * data), and the app is Meridian then.
 */
export function rememberedColours(storage: Pick<LookStorage, 'getItem'> | undefined): ProfileColours {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    return raw ? readColours(JSON.parse(raw)) : {};
  } catch {
    return {};
  }
}

/**
 * The theme for a screen with no LookProvider above it, as the error page is
 * when a provider failed at start: the remembered look, else Meridian. Meridian
 * too when building the remembered look throws, since that may be what failed.
 */
export function rememberedTheme(
  storage: Pick<LookStorage, 'getItem'> | undefined,
  build: (colours: ProfileColours) => Theme = (colours) => buildTheme(paletteFor(colours)),
): Theme {
  try {
    return build(rememberedColours(storage));
  } catch {
    return meridian;
  }
}

export function rememberColours(
  storage: Pick<LookStorage, 'setItem' | 'removeItem'> | undefined,
  colours: ProfileColours,
): void {
  try {
    if (Object.keys(colours).length === 0) storage?.removeItem(STORAGE_KEY);
    else storage?.setItem(STORAGE_KEY, JSON.stringify(colours));
  } catch {
    // Remembering is a convenience; the server's answer applies either way.
  }
}

export const sameColours = (a: ProfileColours, b: ProfileColours): boolean => {
  const left = readColours(a);
  const right = readColours(b);
  const keys = Object.keys(left) as (keyof ProfileColours)[];
  return keys.length === Object.keys(right).length && keys.every((key) => left[key] === right[key]);
};
